<template>
  <!-- Page Content -->
  <main class="no_nav_height d-flex flex-column">
    <section
      class="row f-row justify-content-center h-100 col col-12 text-center"
      style="height:100%; width:100%"
    >
      <!-- Chart -->
      <section class="d-flex flex-column" style="width: 100%;height:100%;">
        <!-- Row: title -->
        <article class="row f-row">
          <div class="markets-chart-title d-flex align-items-center">
            <div
              class="col"
              :class="
                formatVars.screenWidth < 1400 && formatVars.screenWidth > 1100
                  ? 'col-8'
                  : 'col-7'
              "
            >
              <span style="color: var(--textactive)">
                DOT PLOT vs OIS
              </span>
              (FOMC Participants' Monetary Policy Expectations vs. Overnight
              Index Swaps) &nbsp;|&nbsp; Last update:
              <span style="color: var(--textactive)">
                {{ lastUpdate | dateFormat }}
              </span>
            </div>
            <slot name="spot"></slot>
          </div>
        </article>
        <!-- Row: controls and labels -->
        <article
          class="row f-row justify-content-center"
          id="projectionControls"
          style="vertical-align: middle!important;"
        >
          <div
            class="col row-middle-titles"
            :class="formatVars.screenWidth < 1200 ? 'col-11' : 'col-8'"
            style="padding: 5px 60px 0 14px!important;"
          >
            <div
              class="row f-row"
              style="background: var(--background); border: 1px solid var(--lightbackground);"
            >
              <table
                class="table table-sm table-borderless table-data-controls"
                style="width: 100%;"
              >
                <thead>
                  <tr>
                    <th v-for="(d, i) in formatVars.tableFormat" :key="i">
                      {{ d.label }}:
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td
                      v-for="(d, i) in formatVars.tableFormat"
                      :key="i"
                      :style="{ color: d.bdr }"
                      class="dot-plot__description"
                    >
                      <template v-if="d.label === 'Date'">
                        {{ d.value | dateFormat }}
                      </template>
                      <template v-else>
                        {{ d.value | priceFormat }}
                      </template>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </article>
        <!-- Row: legend -->
        <article
          class="row f-row"
          style="position: absolute; left: 18px; top: 100px;"
        >
          <div class="col col-auto text-left" style="z-index: 100000;">
            <table
              class="table table-sm table-responsive table-borderless"
              style="width: 100%; background: rgba(0,0,0,0)"
            >
              <tbody>
                <tr>
                  <td>
                    <i
                      class="fa fa-circle"
                      style="color:var(--expo)"
                      aria-hidden="true"
                    ></i>
                    FOMC's assessments
                  </td>
                  <td v-for="(d, i) in formatVars.tableFormat" :key="i">
                    <template v-if="d.isLegend">
                      <i
                        class="fa fa-minus mr-1"
                        :style="{ color: d.bdr }"
                        aria-hidden="true"
                      >
                      </i>
                      {{ d.label }}
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </article>
        <!-- Row: Chart -->
        <article class="row f-row h-100">
          <aside
            class="col col-12 text-center progress-div"
            v-if="loading.dotPlotProjections || loading.dotsProjections"
          >
            <QSLoading
              :isLoading="loading.dotPlotProjections || loading.dotsProjections"
              :size="35"
              :colorTheme="colorTheme"
            />
          </aside>
          <aside
            class="col col-12 text-center"
            v-else-if="allData['dotPlotProjections'].length === 0"
            style="position: absolute; left: 0; top: 27.5%; z-index: 100000000009;"
          >
            No Data Available
          </aside>
          <section class="col col-12" style="height:96%; width:100%">
            <div
              class="h-100 d-flex flex-column"
              id="dotPlot-div"
              style="width: 100%; height:100%;"
            >
              <svg id="svg-dotPlot"></svg>
            </div>
          </section>
        </article>
      </section>
    </section>
  </main>
</template>

<script>
import { additionalYSpace } from "@/_core/_helpers/_helpers";
import { Chart } from "@/_core/refactor/Chart";
import { select, selectAll, extent, format, timeFormat } from "d3";
import { getDotPlot } from "@/_core/data/api_calls/getDotPlot";
import { DotPlotHover } from "@/_core/charts/DotPlot";
import {
  colorDarkExpoAlpha,
  colorExpo,
  colorExpoAlpha3,
  colorHedgeH,
  colorHedgeAlpha,
  colorFwdF,
  colorNet,
  colorSymmetryH,
  currencyFormat,
  dateFormat,
} from "@/_core/_helpers/_parametric_tools";
import QSLoading from "@/components/QSLoading.vue";

export default {
  name: "DotPlot",
  components: {
    QSLoading,
  },
  props: {
    processData: { required: true },
    colorTheme: { required: true, type: Boolean, default: true },
  },
  data() {
    return {
      allData: {
        dotPlotProjections: [],
        dotsProjections: [],
        copyExtendObjectiveDates: [],
        dotPlotMode: [],
        dotPlotMedian: [],
        dotPlotOISMeeting: [],
        dotPlotOISLastestValue: [],
      },
      chartElements: {
        D3Chart: null,
        svgNode: null,
      },
      formatVars: {
        addDays: 14,
        addDates: [],
        addDatesBefore: [],
        barHeightMode: 3,
        barHeightMedian: 3,
        barWidth: 0.5,
        bckMeeting: colorFwdF(this.colorTheme),
        bckLatestVal: colorNet(this.colorTheme),
        bckMode: colorExpoAlpha3(this.colorTheme),
        bdrMode: colorSymmetryH(this.colorTheme),
        bckMedian: colorHedgeAlpha(this.colorTheme),
        bdrMedian: colorHedgeH(this.colorTheme),
        currencyFormat,
        dateFormat,
        dotColor: colorDarkExpoAlpha(this.colorTheme),
        dotLineColor: colorExpo(this.colorTheme),
        logo: require("@/assets/images/symbol_blue.svg"),
        oisLineWidth: 3,
        statsLineWidth: 3,
        tableFormat: [
          {
            back: "rgba(0,0,0,0)",
            bdr: "rgba(0,0,0,0)",
            data: {},
            label: "...",
            value: "-",
          },
        ],
        txtMedian: colorHedgeH(this.colorTheme),
        txtMode: colorSymmetryH(this.colorTheme),
        radiusDots: 4,
        screenWidth: 0,
      },
      loading: {
        dotPlotProjections: false,
        dotsProjections: false,
      },
      dataHover: {},
    };
  },
  computed: {
    loadingDotPlotProjections() {
      return this.loading["dotPlotProjections"];
    },
    allDataDotPlotProjections() {
      return this.allData["dotPlotProjections"];
    },
    lastUpdate() {
      if (this.allData.dotPlotProjections.length === 0) {
        return "...";
      } else {
        return this.allData.dotsProjections.at(-1).update_date;
      }
    },
  },
  watch: {
    colorTheme() {
      this.dataAndCharts();
    },
    loadingDotPlotProjections(val) {
      if (val === false) {
        this.loading["dotPlotProjections"] = val;
      }
    },
    allDataDotPlotProjections(val) {
      if (val) {
        Object.assign(this.allData, { dotPlotProjections: val });
        this.dataAndCharts();
      }
    },
    dataHover(val) {
      this.dataHover = val;
      this.getDataHover();
    },
  },
  created() {
    this.createTableFormat();
    this.chartElements["D3Chart"] = new Chart("dotPlot");
  },
  mounted() {
    let _this = this;
    this.getAllData();
    this.updateScreenWidth();
    window.addEventListener("resize", function() {
      _this.callWidth();
    });
  },
  methods: {
    createChart() {
      if (this.allData["dotPlotProjections"].length === 0) {
        return;
      }
      // === set SVG canvas
      this.chartElements["D3Chart"].setSVG({
        top: { perc: 0.5, px: 5 },
        right: { perc: 0.5, px: 60 },
        bottom: { perc: 0.5, px: 25 },
        left: { perc: 0.025, px: 12 },
        logo: this.formatVars["logo"],
        colorTheme: this.colorTheme,
      });
      // === extend objective dates
      this.extendObjectiveDates();
      this.getDotPlotMetrics();
      this.dataHover = this.allData["dotPlotProjections"][0];
      this.getDataHover();
      this.allData.copyExtendObjectiveDates.sort(function(a, b) {
        return a.objective_date - b.objective_date;
      });
      const additionalDates = (position) => {
        let dat = new Date(
          this.allData.copyExtendObjectiveDates.at(position).objective_date,
        );
        let day = dat.getDate();
        let days = day === 0 ? 1 : day;
        dat.setDate(days);
        if (position === -1) {
          this.formatVars.addDates = this.processData.createNWeeks(dat, 14);
        } else
          this.formatVars.addDatesBefore = this.processData.createNWeeks(
            dat,
            12,
            "-",
          );
      };
      additionalDates(-1);
      additionalDates(0);
      // === min/max
      let minMaxYAxis = this.getMinMax(this.allData.copyExtendObjectiveDates);
      // === set axis
      this.chartElements["D3Chart"].setAxisDateLin({
        xRange: extent(
          this.formatVars.addDatesBefore.concat(this.formatVars.addDates),
          (d) => d.date,
        ),
        data: this.allData.copyExtendObjectiveDates,
        isXRange: true,
        minMaxYAxis,
        yAxisLeft: false,
      });
      this.chartElements["svgNode"] = this.chartElements["D3Chart"].svg;
      // === lines
      const addLines = (data, bckColor) => {
        return this.chartElements["D3Chart"].addLines({
          type: "line",
          data: this.allData[data],
          color: this.formatVars[bckColor],
          lineWidth: this.formatVars["oisLineWidth"],
          date: "objective_date",
          xIsDate: true,
          fieldLine: "objective_date",
        });
      };
      // === line OIS As of meeting date
      addLines("dotPlotOISMeeting", "bckMeeting");
      // === line OIS latest value
      addLines("dotPlotOISLastestValue", "bckLatestVal");

      let bandWidth =
        this.chartElements["svgNode"].x(
          this.allData.dotPlotMode[1].objective_date,
        ) -
        this.chartElements["svgNode"].x(
          this.allData.dotPlotMode[0].objective_date,
        );
      const hasManyDots = this.allData.dotsProjections.some((d) => d.dots > 12);
      this.formatVars.barWidth = !hasManyDots ? this.formatVars.barWidth : 0.75;
      let candleWidth = bandWidth * this.formatVars.barWidth + 1;
      // === bars
      const addBars = ({
        dataBars,
        customName,
        fieldBackground,
        candleWidth,
        heightBar,
        fieldColor,
      }) => {
        return this.chartElements["D3Chart"].addBars({
          type: "bar",
          data: this.allData[dataBars],
          customName,
          yField: "value",
          fieldColor,
          fieldBackground,
          candleWidth,
          date: "objective_date",
          toZero: false,
          height: `${this.formatVars["radiusDots"] * heightBar + 1}`,
          lineWidth: this.formatVars["statsLineWidth"],
          selectAllElements: `.bars${customName}`,
        });
      };
      // === bars mode
      addBars({
        dataBars: "dotPlotMode",
        customName: "mode",
        fieldBackground: this.formatVars.bckMode,
        fieldColor: this.formatVars.bdrMode,
        candleWidth: candleWidth - 8,
        heightBar: this.formatVars["barHeightMode"],
      });
      // === bars median
      addBars({
        dataBars: "dotPlotMedian",
        customName: "median",
        fieldBackground: this.formatVars.bckMedian,
        fieldColor: this.formatVars.bdrMedian,
        candleWidth,
        heightBar: this.formatVars["barHeightMedian"],
      });
      // === Dot plot
      this.chartElements["D3Chart"].addScatter({
        type: "scatter",
        data: this.allData.copyExtendObjectiveDates,
        xField: "objective_date",
        yField: "value",
        fillColor: this.formatVars["dotColor"],
        lineColor: this.formatVars["dotLineColor"],
        radius: this.formatVars["radiusDots"],
      });
      DotPlotHover(this, this.colorTheme);
    },
    deleteCharts() {
      select("#dotPlot-div")
        .selectAll(".tooltip")
        .remove();
      select("#dotPlot-div")
        .select("svg > *")
        .remove();
      selectAll("circle").remove();
      selectAll(".tooltip").remove();
    },
    extendObjectiveDates() {
      const extendDates = this.allData.dotsProjections;
      const getDay = (date, dots) => {
        const newDate = new Date(date);
        newDate.setDate(
          newDate.getDate() - (this.formatVars.addDays / 2) * (dots - 1),
        );
        return newDate;
      };
      const addDays = (actualDate, position) => {
        const newDate = new Date(actualDate);
        newDate.setDate(newDate.getDate() + position * this.formatVars.addDays);
        return newDate;
      };
      let previousObject = {};
      let position = 1;
      let newDay;
      let dateStart;
      extendDates.forEach((d) => {
        let modifiedDateObject;
        if (d.id !== previousObject.id) {
          position = 1;
          newDay = getDay(d.objective_date, d.dots);
          dateStart = newDay;
          modifiedDateObject = {
            ...d,
            objective_date: newDay,
          };
        } else {
          newDay = addDays(dateStart, position);
          modifiedDateObject = {
            ...d,
            objective_date: newDay,
          };
          position += 1;
        }
        this.allData.copyExtendObjectiveDates.push(modifiedDateObject);
        previousObject = d;
      });
    },
    getDotPlotMedian() {
      this.allData.dotPlotMedian = this.allDataDotPlotProjections.filter(
        (d) => d.name === "median",
      );
    },
    getDotPlotMode() {
      this.allData.dotPlotMode = this.allDataDotPlotProjections.filter(
        (d) => d.name === "mode",
      );
    },
    getDotPlotOISMeeting() {
      this.allData.dotPlotOISMeeting = this.allData.dotsProjections.reduce(
        (accum, objData) => {
          const matchingDot = this.allData.dotPlotProjections.find(
            (d) =>
              d.name === "ois" &&
              +d.update_date === +objData.update_date &&
              !accum.some((obj) => obj.id === d.id),
          );
          if (matchingDot) {
            accum.push(matchingDot);
          }
          return accum;
        },
        [],
      );

      if (
        this.allData.dotPlotOISMeeting.length >
        this.allData.dotPlotMedian.length
      ) {
        this.allData.dotPlotOISMeeting = this.allData.dotPlotOISMeeting.slice(
          0,
          this.allData.dotPlotMedian.length,
        );
      }
    },
    getDotPlotOISLatestValue() {
      this.allData.dotPlotOISLastestValue = this.allData.dotsProjections.reduce(
        (accum, objData) => {
          const matchingDot = this.allData.dotPlotProjections.find(
            (d) =>
              d.name === "ois" &&
              (+d.update_date !== +objData.update_date ||
                +objData.update_date === +new Date()) &&
              !accum.some((obj) => obj.id === d.id),
          );
          if (matchingDot) {
            accum.push(matchingDot);
          }
          return accum;
        },
        [],
      );

      if (
        this.allData.dotPlotOISLastestValue.length >
        this.allData.dotPlotMedian.length
      ) {
        this.allData.dotPlotOISLastestValue = this.allData.dotPlotOISLastestValue.slice(
          this.allData.dotPlotMedian.length,
        );
      }
    },
    getDotPlotMetrics() {
      this.getDotPlotMedian();
      this.getDotPlotMode();
      this.getDotPlotOISMeeting();
      this.getDotPlotOISLatestValue();
    },
    dataAndCharts() {
      if (this.allData.dotPlotProjections.length > 0) {
        this.deleteCharts();
        this.createChart();
      }
    },
    getMinMax(data) {
      let extYAxis = [+Infinity, -Infinity];
      extYAxis = this.processData.minMax(data, "value", "value", extYAxis);
      return additionalYSpace(extYAxis, 0.15);
    },
    createTableFormat() {
      let _this = this;
      Object.assign(this.formatVars, {
        tableFormat: [
          {
            back: "",
            bdr: "",
            data: "",
            value: "...",
            label: "Date",
            isLegend: false,
          },
          {
            back: _this.formatVars["txtMode"],
            bdr: _this.formatVars["txtMode"],
            data: "dotPlotMode",
            value: "0",
            label: "Mode",
            isLegend: true,
          },
          {
            back: _this.formatVars["txtMedian"],
            bdr: _this.formatVars["txtMedian"],
            data: "dotPlotMedian",
            value: "0",
            label: "Median",
            isLegend: true,
          },
          {
            back: _this.formatVars["bckMeeting"],
            bdr: _this.formatVars["bckMeeting"],
            data: "dotPlotOISMeeting",
            value: "0",
            label: "OIS as of meeting date",
            isLegend: true,
          },
          {
            back: _this.formatVars["bckLatestVal"],
            bdr: _this.formatVars["bckLatestVal"],
            data: "dotPlotOISLastestValue",
            value: "0",
            label: "OIS lastest value",
            isLegend: true,
          },
        ],
      });
    },
    getDataHover() {
      const dataHover = this.dataHover;
      this.formatVars.tableFormat.map((d) => {
        if (!d.data) {
          d.value = dataHover.objective_date;
        } else {
          d.value = this.allData[d.data].find(
            (v) => v.groupId === dataHover.groupId,
          ).value;
        }
      });
    },
    updateScreenWidth() {
      this.formatVars.screenWidth = window.innerWidth;
    },
    callWidth() {
      this.updateScreenWidth();
      if (this.allData["dotPlotProjections"].length > 0) {
        this.deleteCharts();
        this.createChart();
      }
    },
    getAllData() {
      this.deleteCharts();
      Object.assign(this.allData, { dotPlotProjections: [] });
      Object.assign(this.allData, { dotsProjections: [] });
      Object.assign(this.allData, { dotPlotMode: [] });
      Object.assign(this.allData, { dotPlotMedian: [] });
      Object.assign(this.allData, { dotPlotOISMeeting: [] });
      Object.assign(this.allData, { dotPlotOISLastestValue: [] });

      getDotPlot(this);
    },
  },
  filters: {
    priceFormat: function(val) {
      return val === null || val === 0 ? "-" : format(",.4r")(val);
    },
    dateFormat: function(val) {
      return val === null ||
        val === "" ||
        val === 0 ||
        +val < +new Date(1980, 1, 1)
        ? "-"
        : val === "..."
        ? val
        : timeFormat("%b %d, %y")(val);
    },
  },
};
</script>

<style scoped>
.dot-plot__description {
  font-size: 15px;
}
.table-data-controls thead tr th {
  font-size: 12px;
  font-weight: normal;
}
</style>
